import { Component, type ErrorInfo, type ReactNode } from 'react';
import errorTracker from '../../../services/errorTracking';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  componentName?: string;
  allowRetry?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorId: string | null;
  retryCount: number;
}

/**
 * OverlayErrorBoundary
 * Phase 3: Error boundary specifically for overlay components
 * Prevents overlay errors from breaking the entire meeting experience
 */
export class OverlayErrorBoundary extends Component<Props, State> {
  private maxRetries = 2;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorId: null, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Track error with comprehensive context
    const errorId = errorTracker.captureComponentError(
      error,
      this.props.componentName || 'OverlayErrorBoundary',
      'overlay_error_boundary',
      {
        additionalData: {
          componentStack: errorInfo.componentStack,
          errorBoundary: 'OverlayErrorBoundary',
          retryCount: this.state.retryCount,
          errorInfo,
        },
      },
    );

    this.setState({ errorId });

    // Log the error for debugging
    console.error('Overlay Error Boundary caught an error:', error, errorInfo);

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    if (this.state.retryCount < this.maxRetries) {
      this.setState((prevState) => ({
        hasError: false,
        error: null,
        errorId: null,
        retryCount: prevState.retryCount + 1,
      }));

      // Track retry attempt
      errorTracker.captureComponentError(
        new Error('Overlay retry attempt'),
        this.props.componentName || 'OverlayErrorBoundary',
        'retry_attempt',
        {
          additionalData: {
            retryCount: this.state.retryCount + 1,
            originalErrorId: this.state.errorId,
          },
        },
      );
    }
  };

  handleDismiss = () => {
    this.setState({
      hasError: false,
      error: null,
      errorId: null,
    });

    // Track dismiss action
    errorTracker.captureComponentError(
      new Error('Overlay error dismissed'),
      this.props.componentName || 'OverlayErrorBoundary',
      'error_dismissed',
      {
        additionalData: {
          originalErrorId: this.state.errorId,
          retryCount: this.state.retryCount,
        },
      },
    );
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI or default
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Enhanced fallback UI with retry and dismiss options
      return (
        <div className="overlayErrorBoundary">
          <div className="overlayErrorTitle">Overlay Error</div>
          <div className="overlayErrorMessage">
            {this.state.error?.message || 'Some overlay features may not be available'}
          </div>

          {this.props.allowRetry !== false && (
            <div className="overlayErrorActions">
              {this.state.retryCount < this.maxRetries && (
                <button
                  type="button"
                  onClick={this.handleRetry}
                  className="btn-retry"
                  title={`Retry (${this.maxRetries - this.state.retryCount} attempts left)`}
                >
                  Retry
                </button>
              )}
              <button
                type="button"
                onClick={this.handleDismiss}
                className="btn-dismiss"
                title="Dismiss this error"
              >
                Dismiss
              </button>
            </div>
          )}

          {this.state.errorId && (
            <div className="overlayErrorId" title="Error ID for support">
              ID: {this.state.errorId.slice(-8)}
            </div>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
